import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DayPicker } from 'react-day-picker';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { format } from 'date-fns';

import { selectLocale } from 'selectors/locale-data';
import { getDateFnsLocaleFromAppLocale } from 'helpers/intl-fns';

import Button from 'components/Button';
import Message from 'components/Message';

import './DatePicker.scss';

const messages = defineMessages({
  apply: {
    id: 'date-range-picker.apply',
    defaultMessage: 'Apply',
  },
  cancel: {
    id: 'date-range-picker.cancel',
    defaultMessage: 'Cancel',
  },
});

const DateRangePicker = ({ className, defaultRange, modifiers, onApply, onCancel }) => {
  const [dateRange, setDateRange] = useState(defaultRange);

  const locale = useSelector(selectLocale);
  const dateFnsLocale = getDateFnsLocaleFromAppLocale(locale);
  // eslint-disable-next-line import/no-dynamic-require
  const dateFnsLocaleFile = require(`date-fns/locale/${dateFnsLocale}/index.js`);

  const handleCancel = () => {
    setDateRange();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={classNames('date-picker', className)}>
      <DayPicker
        data-cypress="react-day-picker"
        enableOutsideDaysClick
        formatters={{
          formatWeekdayName: weekday => format(weekday, 'iiiii', { locale: dateFnsLocaleFile }),
        }}
        locale={dateFnsLocaleFile}
        mode="range"
        modifiers={modifiers}
        onSelect={selectedRange => setDateRange(selectedRange)}
        selected={dateRange}
        showOutsideDays
      />

      <div className="date-picker__footer">
        <Button styled="primary-outline" onClick={handleCancel}>
          <Message message={messages.cancel} />
        </Button>

        <Button disabled={!dateRange} styled onClick={() => onApply(dateRange)}>
          <Message message={messages.apply} />
        </Button>
      </div>
    </div>
  );
};

DateRangePicker.propTypes = {
  className: PropTypes.string,
  dateFnsLocale: PropTypes.shape({}),
  defaultRange: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date),
  }),
  modifiers: PropTypes.shape({
    disabled: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ after: PropTypes.instanceOf(Date) })]),
    today: PropTypes.instanceOf(Date),
  }),
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default DateRangePicker;
